import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const VersionHistory = ({ deploymentId, deploymentName, currentVersionId, onRollback, onClose }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareVersions, setCompareVersions] = useState({ a: null, b: null });
  const [comparison, setComparison] = useState(null);
  const [showCode, setShowCode] = useState(null);

  useEffect(() => {
    fetchVersions();
  }, [deploymentId]);

  const fetchVersions = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/deployments/${deploymentId}/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersions(res.data.versions || []);
    } catch (err) {
      console.error('Error fetching versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async (versionId) => {
    if (!window.confirm('¿Estás seguro de restaurar esta versión? Esto reemplazará el código actual.')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/deployments/${deploymentId}/rollback/${versionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRollback?.();
      fetchVersions();
    } catch (err) {
      console.error('Error rolling back:', err);
      alert('Error al restaurar versión');
    }
  };

  const fetchVersionDetail = async (versionId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/deployments/${deploymentId}/versions/${versionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCode(res.data);
    } catch (err) {
      console.error('Error fetching version detail:', err);
    }
  };

  const compareVersionsAction = async () => {
    if (!compareVersions.a || !compareVersions.b) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(
        `${API}/api/deployments/${deploymentId}/versions/${compareVersions.a}/compare/${compareVersions.b}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComparison(res.data);
    } catch (err) {
      console.error('Error comparing versions:', err);
    }
  };

  const toggleVersionSelect = (versionId) => {
    if (!compareMode) return;
    
    if (!compareVersions.a) {
      setCompareVersions({ ...compareVersions, a: versionId });
    } else if (!compareVersions.b && versionId !== compareVersions.a) {
      setCompareVersions({ ...compareVersions, b: versionId });
    } else if (versionId === compareVersions.a) {
      setCompareVersions({ a: compareVersions.b, b: null });
    } else if (versionId === compareVersions.b) {
      setCompareVersions({ ...compareVersions, b: null });
    }
  };

  useEffect(() => {
    if (compareVersions.a && compareVersions.b) {
      compareVersionsAction();
    } else {
      setComparison(null);
    }
  }, [compareVersions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="version-history-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden">
        
        {/* Header */}
        <div className="p-6 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Historial de Versiones</h2>
            <p className="text-sm text-slate-400">{deploymentName} • {versions.length} versiones</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => { setCompareMode(!compareMode); setCompareVersions({ a: null, b: null }); }}
              className={`px-4 py-2 text-sm rounded-lg transition ${
                compareMode 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}>
              {compareMode ? '✓ Comparando' : '⇄ Comparar'}
            </button>
            <button onClick={onClose}
              className="p-2 text-slate-400 hover:text-white transition">✕</button>
          </div>
        </div>

        {/* Compare Selection Info */}
        {compareMode && (
          <div className="px-6 py-3 bg-purple-500/10 border-b border-purple-500/30">
            <p className="text-sm text-purple-300">
              {!compareVersions.a 
                ? 'Selecciona la primera versión para comparar'
                : !compareVersions.b 
                  ? 'Ahora selecciona la segunda versión'
                  : 'Comparación lista ↓'}
            </p>
          </div>
        )}

        {/* Comparison Result */}
        <AnimatePresence>
          {comparison && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
              <h3 className="text-sm font-medium text-white mb-3">Comparación de Versiones</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-cyan-400">v{comparison.version_a.version_number}</p>
                  <p className="text-xs text-slate-400">{comparison.version_a.size_bytes} bytes</p>
                  <p className="text-xs text-slate-500">{comparison.version_a.lines} líneas</p>
                </div>
                <div className="flex flex-col items-center justify-center">
                  <span className="text-2xl">⇄</span>
                  <p className={`text-sm font-bold ${comparison.comparison.size_diff_bytes > 0 ? 'text-emerald-400' : comparison.comparison.size_diff_bytes < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                    {comparison.comparison.size_diff_bytes > 0 ? '+' : ''}{comparison.comparison.size_diff_bytes} bytes
                  </p>
                  <p className={`text-xs ${comparison.comparison.lines_diff > 0 ? 'text-emerald-400' : comparison.comparison.lines_diff < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                    {comparison.comparison.lines_diff > 0 ? '+' : ''}{comparison.comparison.lines_diff} líneas
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-purple-400">v{comparison.version_b.version_number}</p>
                  <p className="text-xs text-slate-400">{comparison.version_b.size_bytes} bytes</p>
                  <p className="text-xs text-slate-500">{comparison.version_b.lines} líneas</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Versions List */}
        <div className="overflow-y-auto max-h-[50vh] p-6">
          {versions.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              No hay versiones disponibles
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((version) => (
                <motion.div key={version.id}
                  onClick={() => toggleVersionSelect(version.id)}
                  className={`p-4 rounded-xl border transition-all ${
                    version.is_current 
                      ? 'bg-cyan-500/10 border-cyan-500/50' 
                      : compareMode && (compareVersions.a === version.id || compareVersions.b === version.id)
                        ? 'bg-purple-500/10 border-purple-500/50 cursor-pointer'
                        : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
                  } ${compareMode ? 'cursor-pointer' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {compareMode && (
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          compareVersions.a === version.id || compareVersions.b === version.id
                            ? 'border-purple-500 bg-purple-500'
                            : 'border-slate-600'
                        }`}>
                          {(compareVersions.a === version.id || compareVersions.b === version.id) && (
                            <span className="text-white text-xs">
                              {compareVersions.a === version.id ? 'A' : 'B'}
                            </span>
                          )}
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-white">v{version.version_number}</span>
                          {version.is_current && (
                            <span className="px-2 py-0.5 text-xs bg-cyan-500/20 text-cyan-400 rounded-full border border-cyan-500/30">
                              Actual
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-400">
                          {new Date(version.created_at).toLocaleString('es')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-white">{version.size_formatted || `${version.size_bytes || 0} bytes`}</p>
                        {version.changes_summary && (
                          <p className="text-xs text-slate-500">{version.changes_summary}</p>
                        )}
                      </div>
                      
                      {!compareMode && (
                        <div className="flex items-center gap-2">
                          <button onClick={(e) => { e.stopPropagation(); fetchVersionDetail(version.id); }}
                            className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition">
                            Ver
                          </button>
                          {!version.is_current && (
                            <button onClick={(e) => { e.stopPropagation(); handleRollback(version.id); }}
                              className="px-3 py-1.5 text-sm bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 rounded-lg transition">
                              Restaurar
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Code Preview Modal */}
      <AnimatePresence>
        {showCode && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-60 flex items-center justify-center p-4"
            onClick={() => setShowCode(null)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-3xl max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                <h3 className="font-medium text-white">Versión {showCode.version_number}</h3>
                <button onClick={() => setShowCode(null)} className="text-slate-400 hover:text-white">✕</button>
              </div>
              <div className="p-4 overflow-auto max-h-[60vh]">
                <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono">
                  {showCode.html?.substring(0, 5000) || 'Sin código'}
                  {showCode.html?.length > 5000 && '\n\n... (código truncado)'}
                </pre>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VersionHistory;
